/**
 * ComposerContentTypeRules — lightweight rules-display panel for the
 * Composer (post create/edit) flow.
 *
 * Rationale: the Studio has a richer ContentTypeRulesPanel that runs
 * its own debounced preflight call. The Composer already runs
 * preflight on every edit (see ComposerPage `useEffect` at ~L105), so
 * duplicating that logic here would just double the server load.
 * This panel only surfaces the *static* rules for the selected type
 * plus a handful of cheap client-side sanity checks that don't
 * require a round trip.
 *
 * It reuses `CONTENT_TYPE_RULES` from the Studio module so the copy
 * stays in sync across both UIs — a single edit there updates both
 * authoring surfaces.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Info } from 'lucide-react';
import {
  CONTENT_TYPE_RULES,
  type ContentType,
} from '../studio/contentTypeRules';

interface Props {
  contentType: ContentType;
  /** Count of client-side media files staged for upload. Used for the
   *  "Needs media" sanity check on story/reel/carousel. */
  mediaCount: number;
}

const REQUIRES_MEDIA: ContentType[] = ['story', 'reel', 'igtv', 'carousel', 'short'];

const ComposerContentTypeRules: React.FC<Props> = ({ contentType, mediaCount }) => {
  const { t } = useTranslation();
  const ruleSet = CONTENT_TYPE_RULES[contentType] ?? CONTENT_TYPE_RULES.post;

  const violations = useMemo(() => {
    const out: string[] = [];
    if (REQUIRES_MEDIA.includes(contentType) && mediaCount === 0) {
      out.push(
        t(
          'comp_rules_missing_media',
          'This content type requires at least one image or video.'
        )
      );
    }
    return out;
  }, [contentType, mediaCount, t]);

  // "post" is the default and doesn't need a rules panel — only surface
  // when the user picked a structured content type.
  if (contentType === 'post') {
    return null;
  }

  return (
    <div
      role="region"
      aria-label={t('comp_rules_region_label', 'Content type rules')}
      className="rounded-lg border border-slate-200 bg-slate-50 p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Info className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          {t(ruleSet.headlineKey, ruleSet.headlineFallback)}
        </h4>
      </div>

      <ul className="space-y-1 text-xs text-slate-600">
        {ruleSet.rules.map((rule, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <Check
              className="h-3 w-3 mt-0.5 text-slate-400 flex-shrink-0"
              aria-hidden="true"
            />
            <span>{t(rule.i18nKey, rule.fallback)}</span>
          </li>
        ))}
      </ul>

      {violations.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
          {violations.map((v, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-amber-700">
              <AlertCircle
                className="h-3 w-3 mt-0.5 flex-shrink-0"
                aria-hidden="true"
              />
              <span>{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComposerContentTypeRules;
